import React, { useContext, useEffect } from "react";
import ButtonComponent from "../../../components/ButtonComponent";
import {
  NewModal,
  NewModalContent,
  NewModalHeader,
  NewModalTitle,
  NewModalBody,
  NewModalFooter,
  NewModalRef,
} from "../../../components/modal/NewModal/NewModal";

import QuestionIcon from "../../../assets/question.svg";

import {
  Input,
  Popover as PopoverBase,
  PopoverProps as PopoverBaseProps,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  useDisclosure,
} from "@nextui-org/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { Controller, useForm, useWatch } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { SessionContext } from "../../../context/session";
import { useTenantStore } from "../../../stores/tenant/use-tenant-store";
import { FluxoType } from "../../../stores/tenant/tenant.types";

const statesOptions = [
  { label: "Acre", value: "AC" },
  { label: "Alagoas", value: "AL" },
  { label: "Amapá", value: "AP" },
  { label: "Amazonas", value: "AM" },
  { label: "Bahia", value: "BA" },
  { label: "Ceará", value: "CE" },
  { label: "Distrito Federal", value: "DF" },
  { label: "Espírito Santo", value: "ES" },
  { label: "Goiás", value: "GO" },
  { label: "Maranhão", value: "MA" },
  { label: "Mato Grosso", value: "MT" },
  { label: "Mato Grosso do Sul", value: "MS" },
  { label: "Minas Gerais", value: "MG" },
  { label: "Pará", value: "PA" },
  { label: "Paraíba", value: "PB" },
  { label: "Paraná", value: "PR" },
  { label: "Pernambuco", value: "PE" },
  { label: "Piauí", value: "PI" },
  { label: "Rio de Janeiro", value: "RJ" },
  { label: "Rio Grande do Norte", value: "RN" },
  { label: "Rio Grande do Sul", value: "RS" },
  { label: "Rondônia", value: "RO" },
  { label: "Roraima", value: "RR" },
  { label: "Santa Catarina", value: "SC" },
  { label: "São Paulo", value: "SP" },
  { label: "Sergipe", value: "SE" },
  { label: "Tocantins", value: "TO" },
];

const fornecimentoOptions = [
  { label: "Monofásico", value: "Monofásico" },
  { label: "Bifásico", value: "Bifásico" },
  { label: "Trifásico", value: "Trifásico" },
];

const inputClassNames = {
  label: ["text-normal"],
  inputWrapper: ["bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent"],
  input: ["text-base"],
  errorMessage: ["text-left"],
};

type ErrorReadPDFModalProps = {
  handleGetFile: () => FormData | undefined;
  handleSubmitData: (data: FormData) => void;
  isLoading: boolean;
  erro?: string;
};

type GetCepInformationResponse = {
  cep: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
};

const useGetCepInformation = (cep: string) => {
  return useQuery({
    queryKey: ["get-cep-information"],
    queryFn: async ({ queryKey }) => {
      const cepCleanedValue = cep.replace("-", "");
      const response = await axios.get<GetCepInformationResponse>(`https://brasilapi.com.br/api/cep/v1/${cepCleanedValue}`);
      return response.data;
    },
    enabled: !!cep && typeof cep === "string" && cep.length === 9,
    retry: 1,
  });
};

function formatCEP(cep: string): string {
  const cleaned = cep.replace(/\D/g, "");

  if (cleaned.length > 5) {
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 8)}`;
  }

  return cleaned;
}

function formatNumber(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 5);
  const num = parseInt(digits, 10) || 0;

  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

const tipoFornecimentoSchema = z.discriminatedUnion("isTipoRequired", [
  z.object({
    isTipoRequired: z.literal(true),
    tipo: z
      .string()
      .nonempty("Campo Obrigatório.")
      .refine((value) => fornecimentoOptions.some((option) => option.value === value), "Selecione uma opção válida."),
  }),
  z.object({
    isTipoRequired: z.literal(false),
    tipo: z.string().optional(),
  }),
]);

const instalacaoFormSchema = z
  .object({
    numeroInstalacao: z
      .string()
      .trim()
      .nonempty("Campo Obrigatório.")
      .min(3, "Deve ter no mínimo 3 caracteres.")
      .max(20, "Deve ter no máximo 20 caracteres."),
    ultimoConsumo: z
      .string()
      .nonempty("Campo Obrigatório.")
      .refine((value) => {
        const cleaned = value.replace(/\D/g, "");
        const valueAsInt = Number(cleaned);
        return valueAsInt > 1;
      }, "Valor deve ser maior que 1 (Um).")
      .refine((value) => {
        const cleaned = value.replace(/\D/g, "");
        const valueAsInt = Number(cleaned);
        return valueAsInt <= 99_999;
      }, "Valor deve ser menor ou igual que 99.999 kWh."),
    cep: z
      .string()
      .nonempty("Campo Obrigatório.")
      .refine((value) => value.replace(/\D/g, "").length === 8, "Insira um CEP válido."),
    endereco: z.string().trim().nonempty("Campo Obrigatório.").max(100, "Deve ter no máximo 100 caracteres."),
    numero: z.string().trim().nonempty("Campo Obrigatório.").max(10, "Deve ter no máximo 10 caracteres."),
    complemento: z.string().trim().max(20, "Deve ter no máximo 20 caracteres.").optional(),
    bairro: z.string().trim().nonempty("Campo Obrigatório.").max(50, "Deve ter no máximo 50 caracteres."),
    cidade: z.string().trim().nonempty("Campo Obrigatório.").max(50, "Deve ter no máximo 50 caracteres."),
    estado: z.string().trim().nonempty("Campo Obrigatório."),
  })
  .and(tipoFornecimentoSchema);

type InstalacaoFormSchema = z.infer<typeof instalacaoFormSchema>;

type PopoverProps = Omit<PopoverBaseProps, "children">;

const Popover = (props: PopoverProps) => {
  const { onOpen, onOpenChange, isOpen, onClose } = useDisclosure();

  return (
    <PopoverBase {...props} isOpen={isOpen} onOpenChange={onOpenChange} placement="top">
      <PopoverTrigger className="absolute right-0 top-1/2 -translate-y-1/2" onMouseEnter={onOpen} onMouseLeave={onClose}>
        <button>
          <img src={QuestionIcon} alt="Ícone de Pergunta" className="w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-[320px] p-3">
        <p className="text-center text-sm">
          O valor em <span className="italic">kWh</span> corresponde ao total de energia consumida no período de leitura. Na sua fatura,
          você encontra essa informação na seção <strong>"Detalhamento de Consumo"</strong> ou <strong>"Histórico de Consumo"</strong>,
          geralmente identificada como <strong>Consumo (kWh)</strong>.
        </p>
      </PopoverContent>
    </PopoverBase>
  );
};

const ErrorReadPDFModal = React.forwardRef<NewModalRef, ErrorReadPDFModalProps>(({ handleGetFile, handleSubmitData, isLoading, erro }, ref) => {
  const { setItemSession } = useContext(SessionContext);
  const tenant = useTenantStore();

  const form = useForm<InstalacaoFormSchema>({
    resolver: zodResolver(instalacaoFormSchema),
    defaultValues: {
      numeroInstalacao: "",
      ultimoConsumo: "",
      cep: "",
      endereco: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      isTipoRequired: tenant.type === FluxoType.CONSULTOR,
    },
    mode: "all",
  });

  const { "0": cep } = useWatch({
    control: form.control,
    name: ["cep"],
  });

  const getCepInformationRequestCtx = useGetCepInformation(cep);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (getCepInformationRequestCtx.isSuccess) {
      form.setValue("estado", getCepInformationRequestCtx.data.state);
      form.setValue("cidade", getCepInformationRequestCtx.data.city);
      form.setValue("bairro", getCepInformationRequestCtx.data.neighborhood);
      form.setValue("endereco", getCepInformationRequestCtx.data.street);
    }
  }, [getCepInformationRequestCtx.isSuccess, getCepInformationRequestCtx.data, form]);

  const isCepFieldAlreadyFilled =
    (cep.length === 9 || getCepInformationRequestCtx.isSuccess || getCepInformationRequestCtx.isError) &&
    !getCepInformationRequestCtx.isLoading;

  function formatAddress(data: Omit<InstalacaoFormSchema, "numeroInstalacao" | "ultimoConsumo" | "tipo" | "isTipoRequired">): string {
    let address = `${data.endereco}, ${data.numero} - ${data.bairro} / ${data.cidade}, ${data.estado} - CEP ${data.cep}`;

    if (typeof data.complemento === "string" && data.complemento.length > 1) {
      address = address.concat(` - ${data.complemento}`);
    }

    return address.toUpperCase();
  }

  async function handleSubmitFormData() {
    form.trigger();
    if (!form.formState.isValid) return;

    const formData = handleGetFile();
    if (!formData) return;

    const formValues = form.getValues();

    formData.append("NumeroInstalacao", formValues.numeroInstalacao);
    formData.append("UltimoConsumo", formValues.ultimoConsumo);
    if (formValues.tipo) formData.append("TipoFornecimento", formValues.tipo);

    const address = formatAddress({
      cep: formValues.cep,
      endereco: formValues.endereco,
      numero: formValues.numero,
      complemento: formValues.complemento,
      bairro: formValues.bairro,
      cidade: formValues.cidade,
      estado: formValues.estado,
    });
    formData.append("Endereco", address);

    setItemSession(
      "dados-fatura",
      JSON.stringify({
        numeroInstalacao: formValues.numeroInstalacao,
        endereco: address,
        consumoMedio: parseInt(formValues.ultimoConsumo.replace(/\D/, "")),
      })
    );

    await handleSubmitData(formData);
    queryClient.removeQueries({ queryKey: ["get-cep-information"] });
  }

  return (
    <NewModal
      ref={ref}
      isDismissable={false}
      className="max-sm:min-h-screen max-sm:pb-4 max-sm:pt-32 max-w-[592px] max-sm:rounded-none"
      onClose={() => {
        form.reset();
        queryClient.removeQueries({ queryKey: ["get-cep-information"] });
      }}
    >
      <NewModalContent>
        <NewModalHeader>
          <NewModalTitle>Preencha as informações do seu imóvel</NewModalTitle>
        </NewModalHeader>
        <NewModalBody>
          <div className="grid grid-cols-1 gap-3 mt-3">
            <Controller
              name="numeroInstalacao"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  label="Número da Instalação"
                  color="primary"
                  size="md"
                  errorMessage={fieldState.error?.message}
                  isInvalid={fieldState.invalid}
                  {...field}
                  autoFocus={true}
                  classNames={inputClassNames}
                />
              )}
            />

            <Controller
              name="ultimoConsumo"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  label="Último Consumo (kWh)"
                  color="primary"
                  size="md"
                  errorMessage={fieldState.error?.message}
                  isInvalid={fieldState.invalid}
                  {...field}
                  autoComplete="off"
                  onChange={({ target }) => field.onChange(formatNumber(target.value))}
                  classNames={{ ...inputClassNames, innerWrapper: ["relative"] }}
                  endContent={<Popover />}
                />
              )}
            />

            <Controller
              control={form.control}
              name="tipo"
              render={({ field: { onChange, value, ...otherFieldProps }, fieldState }) => (
                <Select
                  label="Tipo de Fornecimento"
                  isInvalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                  value={value}
                  onChange={onChange}
                  {...otherFieldProps}
                  placeholder={tenant.type === FluxoType.CONSULTOR ? "Ex.: Monofásico, Bifásico, etc." : "Ex.: Monofásico, Bifásico, etc. (Opcional)"}
                  items={fornecimentoOptions}
                  classNames={{
                    listbox: "max-h-60 overflow-auto",
                    label: ["text-normal"],
                    value: ["text-base"],
                    errorMessage: ["text-left"],
                    trigger: ["bg-white"]
                  }}
                >
                  {(option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  )}
                </Select>
              )}
            />
          </div>

          <div className="flex flex-col gap-2 md:gap-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
              <Controller
                name="cep"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Input
                    label="CEP"
                    maxLength={9}
                    color="primary"
                    size="md"
                    errorMessage={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    {...field}
                    onChange={({ target }) => field.onChange(formatCEP(target.value))}
                    classNames={{
                      label: ["text-normal"],
                      inputWrapper: ["bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent "],
                      input: ["text-base"],
                      errorMessage: ["text-left"],
                    }}
                  />
                )}
              />

              <Controller
                name="numero"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Número"
                    color="primary"
                    size="md"
                    isDisabled={!isCepFieldAlreadyFilled}
                    errorMessage={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    {...field}
                    classNames={{
                      label: ["text-normal"],
                      inputWrapper: ["bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent "],
                      input: ["text-base"],
                      errorMessage: ["text-left"],
                    }}
                  />
                )}
              />
            </div>

            <Controller
              name="endereco"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  label="Endereço"
                  color="primary"
                  size="md"
                  isDisabled={!isCepFieldAlreadyFilled}
                  errorMessage={fieldState.error?.message}
                  isInvalid={fieldState.invalid}
                  {...field}
                  classNames={inputClassNames}
                />
              )}
            />

            <Controller
              name="complemento"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  label="Complemento (Opcional)"
                  color="primary"
                  size="md"
                  isDisabled={!isCepFieldAlreadyFilled}
                  errorMessage={fieldState.error?.message}
                  isInvalid={fieldState.invalid}
                  {...field}
                  classNames={inputClassNames}
                />
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3">
              <Controller
                name="bairro"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Bairro"
                    color="primary"
                    size="md"
                    isDisabled={!isCepFieldAlreadyFilled}
                    errorMessage={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    {...field}
                    classNames={{
                      label: ["text-normal"],
                      inputWrapper: ["bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent "],
                      input: ["text-base"],
                      errorMessage: ["text-left"],
                    }}
                  />
                )}
              />

              <Controller
                name="cidade"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Cidade"
                    color="primary"
                    size="md"
                    isDisabled={!isCepFieldAlreadyFilled}
                    errorMessage={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    {...field}
                    classNames={{
                      label: ["text-normal"],
                      inputWrapper: ["bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent "],
                      input: ["text-base"],
                      errorMessage: ["text-left"],
                    }}
                  />
                )}
              />

              <Controller
                name="estado"
                control={form.control}
                render={({ field: { onChange, value, ...fieldProps }, fieldState }) => (
                  <Select
                    label="Estado"
                    items={statesOptions}
                    isInvalid={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                    isDisabled={!isCepFieldAlreadyFilled}
                    {...fieldProps}
                    placeholder="Selecione seu estado"
                    onChange={onChange}
                    selectedKeys={[value]}
                    classNames={{
                      listbox: "max-h-60 overflow-auto",
                      label: ["text-normal"],
                      value: ["text-base"],
                      errorMessage: ["text-left"],
                    }}
                  >
                    {(option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    )}
                  </Select>
                )}
              />
            </div>
          </div>
        </NewModalBody>
        <NewModalFooter>
          {erro && <p className="error-error-file">{erro}</p>}
          <ButtonComponent className={`continue-button`} onClick={handleSubmitFormData} isDisabled={!form.formState.isValid || isLoading}>
            Enviar
          </ButtonComponent>
        </NewModalFooter>
      </NewModalContent>
    </NewModal>
  );
});

export default ErrorReadPDFModal;
